import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import {
  fetchMaintenanceConfig,
  saveMaintenanceConfig,
  parseWhitelist,
  DEFAULT_MAINTENANCE_CONFIG
} from '../../utils/maintenance';

/**
 * Super admin control panel for maintenance mode: the switch, the message everyone else sees, and
 * the list of emails that keep working while it is on.
 */
const MaintenanceSettings = () => {
  const [config, setConfig] = useState(null);
  const [whitelistText, setWhitelistText] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetchMaintenanceConfig()
      .then((loaded) => {
        setConfig(loaded);
        setWhitelistText(loaded.whitelist.join('\n'));
      })
      .catch((error) => {
        console.error('Error loading maintenance config:', error);
        setConfig({ ...DEFAULT_MAINTENANCE_CONFIG });
        setWhitelistText(DEFAULT_MAINTENANCE_CONFIG.whitelist.join('\n'));
        setStatus({ variant: 'danger', message: 'Could not load the saved settings. Showing defaults.' });
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const saved = await saveMaintenanceConfig({ ...config, whitelist: parseWhitelist(whitelistText) });
      setConfig(saved);
      setWhitelistText(saved.whitelist.join('\n'));
      setStatus({
        variant: 'success',
        message: saved.enabled
          ? `Maintenance mode is ON. ${saved.whitelist.length} whitelisted email(s) plus all super admins still have access.`
          : 'Maintenance mode is OFF. Everyone has normal access.'
      });
    } catch (error) {
      console.error('Error saving maintenance config:', error);
      setStatus({ variant: 'danger', message: 'Save failed. Nothing was changed.' });
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <Card>
        <Card.Body className="text-center">
          <Spinner animation="border" role="status" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header className="bg-primary text-white">
        <h4 className="mb-0">Maintenance Mode</h4>
      </Card.Header>
      <Card.Body>
        {status && <Alert variant={status.variant}>{status.message}</Alert>}

        <Alert variant={config.enabled ? 'warning' : 'secondary'}>
          Currently <strong>{config.enabled ? 'ON' : 'OFF'}</strong>. While it is on, everyone sees the
          message below except the whitelisted emails and super administrators — so testing can carry on.
        </Alert>

        <Form.Check
          type="switch"
          id="maintenance-enabled"
          className="mb-3"
          label="Show the maintenance message to everyone not whitelisted"
          checked={config.enabled}
          onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
        />

        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            value={config.title}
            onChange={(e) => setConfig({ ...config, title: e.target.value })}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Message</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            value={config.message}
            onChange={(e) => setConfig({ ...config, message: e.target.value })}
          />
          <Form.Text className="text-muted">Line breaks are preserved on the maintenance page.</Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Whitelisted emails</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={whitelistText}
            onChange={(e) => setWhitelistText(e.target.value)}
            placeholder="one email per line"
          />
          <Form.Text className="text-muted">
            One per line (commas work too). These accounts keep full access while maintenance is on.
          </Form.Text>
        </Form.Group>

        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="link" href="/maintenance" target="_blank" rel="noreferrer">
          Preview the page
        </Button>
      </Card.Body>
    </Card>
  );
};

export default MaintenanceSettings;
